/**
 * HitzLink Pricing v2 — client mirror of server/config/pricing.js
 *
 * Kept in lockstep with the server. If you change USD prices, exchange
 * rate, or plan IDs here, change them in server/config/pricing.js too —
 * payments.js validates incoming planId against its own copy.
 */

export const USD_TO_THB_RATE = 35;

export type Tier = 'free' | 'pro' | 'agency' | 'enterprise';
export type Cycle = 'monthly' | 'yearly';

export type PlanId =
  | 'pro_monthly'
  | 'pro_yearly'
  | 'agency_monthly'
  | 'agency_yearly'
  | 'enterprise_monthly'
  | 'enterprise_yearly';

export interface PaidPlan {
  tier: Tier;
  cycle: Cycle;
  usd: number;
  durationDays: number;
  name: string;
}

export const PLANS: Record<PlanId, PaidPlan> = {
  pro_monthly: { tier: 'pro', cycle: 'monthly', usd: 59, durationDays: 30, name: 'Pro Monthly' },
  pro_yearly: { tier: 'pro', cycle: 'yearly', usd: 566, durationDays: 365, name: 'Pro Yearly' },
  agency_monthly: { tier: 'agency', cycle: 'monthly', usd: 149, durationDays: 30, name: 'Agency Monthly' },
  agency_yearly: { tier: 'agency', cycle: 'yearly', usd: 1430, durationDays: 365, name: 'Agency Yearly' },
  enterprise_monthly: { tier: 'enterprise', cycle: 'monthly', usd: 449, durationDays: 30, name: 'Enterprise Monthly' },
  enterprise_yearly: { tier: 'enterprise', cycle: 'yearly', usd: 4310, durationDays: 365, name: 'Enterprise Yearly' },
};

export function usdToThb(usd: number): number {
  return Math.round(usd * USD_TO_THB_RATE);
}

export function getPlan(planId: PlanId): PaidPlan {
  return PLANS[planId];
}

export function tierOrder(tier: Tier): number {
  return { free: 0, pro: 1, agency: 2, enterprise: 3 }[tier];
}

export function isUpgrade(from: Tier, to: Tier): boolean {
  return tierOrder(to) > tierOrder(from);
}

/**
 * Format USD price for display: $59 (no decimals if whole), $566 etc.
 * Yearly prices are >$500 so no need for thousands separators yet —
 * if pricing scales above $10k revisit with Intl.NumberFormat.
 */
export function formatUsd(usd: number): string {
  return `$${usd.toLocaleString('en-US')}`;
}

export function formatThb(thb: number): string {
  return `฿${thb.toLocaleString('th-TH')}`;
}

/**
 * "Effective monthly" price for the yearly toggle UI. Yearly is billed
 * once but customers compare against monthly — this lets the card show
 * "$47/mo billed annually" instead of forcing them to divide.
 */
export function effectiveMonthlyUsd(planId: PlanId): number {
  const plan = PLANS[planId];
  if (plan.cycle === 'monthly') return plan.usd;
  return Math.round(plan.usd / 12);
}

/* ----------------------------------------------------------------- */
/*  Phase 4 — Add-on slot catalog                                     */
/* ----------------------------------------------------------------- */

/**
 * Kept in lockstep with server/config/pricing.js#ADDONS. The server
 * is authoritative on price (payments validates against its own
 * copy); this mirror is for the FE rendering layer only.
 */

export type AddonType = 'page_slot' | 'domain_slot' | 'storage_pack' | 'ai_pack';
export type AddonCycle = 'monthly' | 'yearly' | 'one_shot';

export type AddonId =
  | 'page_slot_monthly'
  | 'page_slot_yearly'
  | 'domain_slot_monthly'
  | 'domain_slot_yearly'
  | 'storage_pack_monthly'
  | 'storage_pack_yearly'
  | 'ai_pack_50'
  | 'ai_pack_200';

export interface Addon {
  id: AddonId;
  type: AddonType;
  cycle: AddonCycle;
  usd: number;
  durationDays: number | null;
  name: string;
  payload?: { mb?: number; gens?: number };
}

export const ADDONS: Record<AddonId, Addon> = {
  page_slot_monthly: {
    id: 'page_slot_monthly',
    type: 'page_slot',
    cycle: 'monthly',
    usd: 9,
    durationDays: 30,
    name: '+1 Landing Page Slot',
  },
  page_slot_yearly: {
    id: 'page_slot_yearly',
    type: 'page_slot',
    cycle: 'yearly',
    usd: 89,
    durationDays: 365,
    name: '+1 Landing Page Slot (yearly)',
  },
  domain_slot_monthly: {
    id: 'domain_slot_monthly',
    type: 'domain_slot',
    cycle: 'monthly',
    usd: 5,
    durationDays: 30,
    name: '+1 Custom Domain Slot',
  },
  domain_slot_yearly: {
    id: 'domain_slot_yearly',
    type: 'domain_slot',
    cycle: 'yearly',
    usd: 49,
    durationDays: 365,
    name: '+1 Custom Domain Slot (yearly)',
  },
  storage_pack_monthly: {
    id: 'storage_pack_monthly',
    type: 'storage_pack',
    cycle: 'monthly',
    usd: 7,
    durationDays: 30,
    name: '+10 GB Storage',
    payload: { mb: 10240 },
  },
  storage_pack_yearly: {
    id: 'storage_pack_yearly',
    type: 'storage_pack',
    cycle: 'yearly',
    usd: 69,
    durationDays: 365,
    name: '+10 GB Storage (yearly)',
    payload: { mb: 10240 },
  },
  ai_pack_50: {
    id: 'ai_pack_50',
    type: 'ai_pack',
    cycle: 'one_shot',
    usd: 25,
    durationDays: null,
    name: '+50 AI Generations',
    payload: { gens: 50 },
  },
  ai_pack_200: {
    id: 'ai_pack_200',
    type: 'ai_pack',
    cycle: 'one_shot',
    usd: 89,
    durationDays: null,
    name: '+200 AI Generations',
    payload: { gens: 200 },
  },
};

export const ADDON_IDS: AddonId[] = [
  'page_slot_monthly',
  'page_slot_yearly',
  'domain_slot_monthly',
  'domain_slot_yearly',
  'storage_pack_monthly',
  'storage_pack_yearly',
  'ai_pack_50',
  'ai_pack_200',
];

export function getAddon(addonId: AddonId): Addon {
  return ADDONS[addonId];
}

export function isAddonId(value: string): value is AddonId {
  return Object.prototype.hasOwnProperty.call(ADDONS, value);
}
